/**
 * Home Page - Mobile Responsive
 * Layout per wireframe:
 *   - Nav bar: Subterra logo | Find · News · Opportunities | Sign In/Out · My Account
 *   - Mobile: Upcoming events (horizontal scroll, 1.75 cards visible) → Updates (vertical scroll)
 *   - Desktop: Left half: "Updates" | Right half: "Event Calendar" (7-day grid)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { geocodeCityState } from '../utils/geocoding';
import { formatDateShort, formatTime } from '../utils/dateFormatter';
import Navbar from '../components/Navbar';
import { setNavigationSource } from '../utils/navigationHistory';
import ProfilePostCard from '../components/ProfilePostCard';
import '../styles/colors.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const GENRE_LIST = [
  'Ambient', 'Alternative', 'Blues', 'Classical', 'Country', 'Cultural',
  'Dance', 'EDM', 'Electronic', 'Emo', 'Experimental', 'Folk', 'Grunge',
  'Hardcore', 'Hip-hop', 'Instrumental', 'Jazz', 'Metal', 'Noise', 'Pop',
  'Psychedelic', 'Punk', 'R&B', 'Rap', 'Reggae-Ska', 'Religious', 'Rock',
  'Shoegaze', 'Singer-Songwriter', 'Spoken Word'
];

const DAY_MS = 24 * 60 * 60 * 1000;

// Build an array of Date objects for the next N days starting today
const buildDayRange = (days) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Array.from({ length: days }, (_, i) => new Date(today.getTime() + i * DAY_MS));
};

const isSameDay = (eventDateStr, dayDate) => {
  const dateOnly = eventDateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  return (
    year === dayDate.getFullYear() &&
    month - 1 === dayDate.getMonth() &&
    day === dayDate.getDate()
  );
};

const fmtDayLabel = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });


// ─── Event Calendar ───────────────────────────────────────────────────────────

const EventCalendar = ({ userCity, userState, userTravelDistance }) => {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state — with sessionStorage persistence and user defaults
  const [dateRange, setDateRange] = useState('7days');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState(() => {
    const saved = sessionStorage.getItem('homePage_eventFilters');
    return saved ? JSON.parse(saved).selectedGenres || [] : [];
  });
  const [selectedAges, setSelectedAges] = useState(() => {
    const saved = sessionStorage.getItem('homePage_eventFilters');
    return saved ? JSON.parse(saved).selectedAges || [] : [];
  });
  const [locationCity, setLocationCity] = useState(() => {
    const saved = sessionStorage.getItem('homePage_eventFilters');
    if (saved) return JSON.parse(saved).locationCity || '';
    return userCity || '';
  });
  const [locationState, setLocationState] = useState(() => {
    const saved = sessionStorage.getItem('homePage_eventFilters');
    if (saved) return JSON.parse(saved).locationState || '';
    return userState || '';
  });
  const [filterRadius, setFilterRadius] = useState(() => {
    const saved = sessionStorage.getItem('homePage_eventFilters');
    if (saved) return JSON.parse(saved).filterRadius || '';
    return userTravelDistance ? String(userTravelDistance) : '';
  });
  const [sortBy, setSortBy] = useState('date');
  const [locationError, setLocationError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [filterLat, setFilterLat] = useState(() => {
    const saved = sessionStorage.getItem('homePage_eventFilters');
    return saved ? JSON.parse(saved).filterLat || null : null;
  });
  const [filterLon, setFilterLon] = useState(() => {
    const saved = sessionStorage.getItem('homePage_eventFilters');
    return saved ? JSON.parse(saved).filterLon || null : null;
  });

  const dayCount = dateRange === '7days' ? 7 : dateRange === '14days' ? 14 : 30;
  const days = buildDayRange(dayCount);

  const buildQueryParams = (lat, lon) => {
    const params = new URLSearchParams();
    if (dateRange) params.append('dateRange', dateRange);
    if (selectedGenres.length > 0) params.append('genres', selectedGenres.join(','));
    if (selectedAges.length > 0) params.append('ageRestriction', selectedAges.join(','));
    params.append('sortBy', sortBy);
    if (lat && lon && filterRadius) {
      params.append('filterLat', lat);
      params.append('filterLon', lon);
      params.append('filterRadius', filterRadius);
    }
    return params.toString();
  };

  const fetchEvents = useCallback(async (lat = filterLat, lon = filterLon) => {
    setLoading(true);
    try {
      const qs = buildQueryParams(lat, lon);
      const response = await apiClient.get(`/events?${qs}`);
      if (response.data.success) setEvents(response.data.events);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedGenres, selectedAges, sortBy, filterRadius, filterLat, filterLon]);

  // Save filters to sessionStorage
  useEffect(() => {
    const filters = {
      selectedGenres,
      selectedAges,
      locationCity,
      locationState,
      filterRadius,
      filterLat,
      filterLon
    };
    sessionStorage.setItem('homePage_eventFilters', JSON.stringify(filters));
  }, [selectedGenres, selectedAges, locationCity, locationState, filterRadius, filterLat, filterLon]);

  // Auto-apply saved location on mount + sort by distance if location active
  useEffect(() => {
    const autoApplyUserLocation = async () => {
      if (filterLat && filterLon && filterRadius) {
        fetchEvents(filterLat, filterLon);
      } else if (locationCity && locationState && filterRadius && !filterLat && !filterLon) {
        try {
          const result = await geocodeCityState(locationCity, locationState);
          setFilterLat(result.latitude);
          setFilterLon(result.longitude);
          await fetchEvents(result.latitude, result.longitude);
        } catch (err) {
          console.error('Failed to auto-geocode user location:', err);
          fetchEvents();
        }
      } else {
        fetchEvents();
      }
    };
    
    autoApplyUserLocation();
  }, []);

  const handleApplyFilters = async () => {
    setLocationError('');
    if (locationCity && locationState) {
      if (!filterRadius) { 
        setLocationError('Enter a radius when filtering by location.'); 
        return; 
      }
      
      if (locationCity.trim().length < 2) {
        setLocationError('City name too short');
        return;
      }
      if (locationState.length !== 2) {
        setLocationError('State must be 2 letters (e.g., CT, NY)');
        return;
      }
      
      setLocationLoading(true);
      try {
        const result = await geocodeCityState(locationCity.trim(), locationState.toUpperCase());
        setFilterLat(result.latitude);
        setFilterLon(result.longitude);
        await fetchEvents(result.latitude, result.longitude);
        setLocationError('');
      } catch (err) {
        setLocationError(err.message || 'Invalid location. Please check city and state.');
      } finally {
        setLocationLoading(false);
      }
    } else {
      setFilterLat(null); 
      setFilterLon(null);
      await fetchEvents(null, null);
    }
  };

  // Auto-apply location filter when all 3 fields are filled and valid
  const handleLocationBlur = async () => {
    // Check if all three fields have values
    if (locationCity && locationState && filterRadius) {
      // Validate before attempting
      if (locationCity.trim().length >= 2 && locationState.length === 2 && parseInt(filterRadius) > 0) {
        await handleApplyFilters();
      }
    } else if (!locationCity && !locationState && !filterRadius) {
      // If all fields are empty, clear location filter
      setFilterLat(null);
      setFilterLon(null);
      setLocationError('');
      await fetchEvents(null, null);
    }
  };

  const handleClearFilters = () => {
    setSelectedGenres([]);
    setSelectedAges([]);
    setLocationCity(userCity || '');
    setLocationState(userState || '');
    setFilterRadius(userTravelDistance ? String(userTravelDistance) : '');
    setSortBy('date');
    setFilterLat(null);
    setFilterLon(null);
    setLocationError('');
    fetchEvents(null, null);
  };

  const activeFilterCount = () => {
    let n = 0;
    if (selectedGenres.length > 0) n++;
    if (selectedAges.length > 0) n++;
    // Location filter removed from count since it's always visible
    if (sortBy !== 'date') n++;
    return n;
  };

  const toggleGenre = (g) => setSelectedGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  const toggleAge = (a) => setSelectedAges(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

  // Helper: Calculate distance between two lat/lon points (Haversine formula)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Bucket events into days, sorted by distance if location filter is active
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const futureDays = days.filter(day => day >= today);
  
  const eventsByDay = futureDays.map(day => {
    let dayEvents = events.filter(e => isSameDay(e.event_date, day));
    
    // Sort by distance if location filter is active
    if (filterLat && filterLon) {
      dayEvents = dayEvents.sort((a, b) => {
        if (!a.latitude || !a.longitude) return 1;
        if (!b.latitude || !b.longitude) return -1;
        const distA = getDistance(filterLat, filterLon, parseFloat(a.latitude), parseFloat(a.longitude));
        const distB = getDistance(filterLat, filterLon, parseFloat(b.latitude), parseFloat(b.longitude));
        return distA - distB;
      });
    }
    
    return { day, events: dayEvents };
  });

  return (
    <>
      <style>{`
        /* Desktop styles (default) */
        .event-calendar-container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .calendar-header {
          background-color: var(--coal-black);
          color: var(--text-light);
          padding: 18px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 8px 8px 0 0;
        }

        .location-bar {
          background-color: var(--smoke-gray);
          padding: 12px 24px;
          border-radius: 8px;
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .calendar-grid {
          flex: 1;
          overflow-x: auto;
          border: 1px solid var(--border-light);
          border-radius: 0 0 8px 8px;
          background-color: var(--bg-card);
        }

        .desktop-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          background-color: var(--border-light);
          height: 100%;
          min-height: 500px;
        }

        .day-cell {
          background-color: var(--bg-card);
        }

        .day-header {
          color: var(--text-dark);
          background-color: var(--bg-hover);
          border-bottom: 2px solid var(--border-light);
        }

        .mobile-scroll {
          display: none;
        }

        .filter-panel {
          background-color: var(--bg-card);
          padding: 24px;
          border: 1px solid var(--border-light);
          border-top: none;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .event-calendar-container {
            height: auto;
            max-width: 100vw;
          }

          .calendar-header {
            padding: 16px 0;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            border-radius: 0;
          }

          .calendar-header h2 {
            font-size: 1.5rem !important;
          }

          .calendar-header-controls {
            width: 100%;
            justify-content: flex-start !important;
            gap: 12px;
          }

          .location-bar {
            padding: 12px 12px 12px 0;
            margin-bottom: 16px;
            border-radius: 0;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            width: fit-content;
          }

          .location-inputs {
            display: flex;
            gap: 8px;
          }

          .location-bar input {
            font-size: 16px !important;
            padding: 8px !important;
          }

          .calendar-grid {
            border-radius: 0;
            border-left: none;
            border-right: none;
            overflow-x: visible;
            background-color: var(--coal-black);
          }

          .desktop-grid {
            display: none;
          }

          .mobile-scroll {
            display: flex;
            overflow-x: auto;
            gap: 16px;
            padding: 20px 0;
            padding-right: 8px;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            background-color: var(--coal-black);
            max-width: calc(100vw - 32px);
          }

          .mobile-scroll::-webkit-scrollbar {
            display: none;
          }

          .mobile-event-card {
            flex: 0 0 70%;
            scroll-snap-align: start;
          }

          .filter-panel {
            padding: 20px 0;
            background-color: var(--bg-card);
          }

          .filter-section {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .filter-checkboxes {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .filter-checkbox-label {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 15px;
            cursor: pointer;
            color: var(--text-dark);
          }

          .filter-checkbox-label input[type="checkbox"] {
            width: 20px;
            height: 20px;
            cursor: pointer;
          }
        }
      `}</style>

      <div className="event-calendar-container">
        {/* Calendar header */}
        <div className="calendar-header">
          <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '600' }}>Upcoming Events</h2>
          <div className="calendar-header-controls" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{ 
                padding: '8px 14px', 
                borderRadius: '4px', 
                border: '1px solid var(--border-light)', 
                fontSize: '14px', 
                cursor: 'pointer',
                backgroundColor: 'var(--bg-card)',
                color: 'var(--text-dark)'
              }}
            >
              <option value="7days">Next 7 days</option>
              <option value="14days">Next 14 days</option>
              <option value="30days">Next 30 days</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '8px 14px',
                borderRadius: '4px',
                border: '1px solid var(--border-light)',
                background: activeFilterCount() > 0 ? 'var(--accent-primary)' : 'var(--bg-card)',
                color: activeFilterCount() > 0 ? 'var(--text-dark)' : 'var(--text-dark)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {showFilters ? 'Hide Filters' : `Filters${activeFilterCount() > 0 ? ` (${activeFilterCount()})` : ''}`}
            </button>
          </div>
        </div>

        {/* Location Bar - Below header controls */}
        <div className="location-bar">
          <div className="location-inputs" style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="City"
              value={locationCity}
              onChange={(e) => setLocationCity(e.target.value)}
              onBlur={handleLocationBlur}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', flex: 1, minWidth: '60px', maxWidth: '206px' }}
            />
            <input
              type="text"
              placeholder="ST"
              value={locationState}
              onChange={(e) => setLocationState(e.target.value.toUpperCase())}
              onBlur={handleLocationBlur}
              maxLength={2}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', width: '47px' }}
            />
            <input
              type="number"
              placeholder="Radius (mi)"
              value={filterRadius}
              onChange={(e) => setFilterRadius(e.target.value)}
              onBlur={handleLocationBlur}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', width: '53px' }}
            />
          </div>
          {locationError && <div style={{ color: 'var(--accent-danger)', fontSize: '13px', width: '100%' }}>{locationError}</div>}
          {locationLoading && <div style={{ color: 'var(--text-muted)', fontSize: '13px', width: '100%' }}>Locating...</div>}
        </div>

        {/* Filter Panel - Genres & Age only */}
        {showFilters && (
          <div className="filter-panel">
            {/* Genre filters */}
            <div className="filter-section">
              <label style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-dark)', marginBottom: '8px', display: 'block' }}>
                Genres {selectedGenres.length > 0 && <span style={{ color: 'var(--accent-primary)' }}>({selectedGenres.length} selected)</span>}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto', paddingRight: '8px' }} className="genre-scroll">
                {GENRE_LIST.map(genre => (
                  <label key={genre} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-dark)' }}>
                    <input
                      type="checkbox"
                      checked={selectedGenres.includes(genre)}
                      onChange={() => toggleGenre(genre)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span>{genre}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Age restriction filters */}
            <div className="filter-section">
              <label style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-dark)' }}>Age Restriction</label>
              <div className="filter-checkboxes">
                <label className="filter-checkbox-label" style={{ color: 'var(--text-dark)' }}>
                  <input
                    type="checkbox"
                    checked={selectedAges.includes('all_ages')}
                    onChange={() => toggleAge('all_ages')}
                  />
                  <span>All Ages</span>
                </label>
                <label className="filter-checkbox-label" style={{ color: 'var(--text-dark)' }}>
                  <input
                    type="checkbox"
                    checked={selectedAges.includes('18_plus')}
                    onChange={() => toggleAge('18_plus')}
                  />
                  <span>18+</span>
                </label>
                <label className="filter-checkbox-label" style={{ color: 'var(--text-dark)' }}>
                  <input
                    type="checkbox"
                    checked={selectedAges.includes('21_plus')}
                    onChange={() => toggleAge('21_plus')}
                  />
                  <span>21+</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleClearFilters}
                className="btn btn-secondary"
                style={{ padding: '10px 20px', fontSize: '15px' }}
              >
                Clear Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="btn btn-primary"
                style={{ padding: '10px 24px', fontSize: '15px' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Calendar content */}
        <div className="calendar-grid">
          {loading ? (
            <div style={{ padding: '50px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading events...</div>
          ) : (
            <>
              {/* Desktop: Calendar grid (unchanged) */}
              <div className="desktop-grid">
                {eventsByDay.map(({ day, events: dayEvents }) => (
                  <div key={day.toISOString()} className="day-cell" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}>
                    <div className="day-header" style={{
                      padding: '12px 8px',
                      fontSize: '13px',
                      fontWeight: '700',
                      textAlign: 'center'
                    }}>
                      {fmtDayLabel(day)}
                    </div>

                    <div style={{ 
                      flex: 1, 
                      padding: '8px', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px', 
                      overflowY: 'auto', 
                      overflowX: 'hidden' 
                    }}>
                      {dayEvents.map(event => {
                        const venue = event.collaborators?.find(c => c.collaborator_role === 'venue');
                        const venueName = venue?.collaborator_name || 'TBA';
                        const bands = event.collaborators?.filter(c => c.collaborator_role === 'band') || [];
                        
                        return (
                          <div
                            key={event.id}
                            onClick={() => {
                              setNavigationSource('homepage-calendar');
                              navigate(`/events/${event.id}`);
                            }}
                            style={{
                              backgroundColor: 'var(--coal-black)',
                              color: 'var(--text-light)',
                              borderRadius: '6px',
                              padding: '10px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              lineHeight: '1.4',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              border: '1px solid var(--border-dark)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--smoke-gray)';
                              e.currentTarget.style.borderColor = 'var(--accent-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--coal-black)';
                              e.currentTarget.style.borderColor = 'var(--border-dark)';
                            }}
                          >
                            {event.flyer_url && (
                              <img
                                src={event.flyer_url}
                                alt={event.title}
                                style={{
                                  width: '100%',
                                  height: '70px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  marginBottom: '4px'
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            
                            {event.door_time && (
                              <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--accent-primary)' }}>
                                🚪 {formatTime(event.door_time)}
                              </div>
                            )}
                            
                            <div style={{ fontSize: '11px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              📍 {venueName}
                            </div>
                            
                            {event.city && event.state && (
                              <div style={{ fontSize: '10px', opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {event.city}, {event.state}
                              </div>
                            )}
                            
                            {bands.length > 0 && (
                              <div style={{ fontSize: '10px', fontWeight: '600', opacity: 0.9 }}>
                                🎸 {bands.slice(0, 2).map(b => b.collaborator_name).join(', ')}
                                {bands.length > 2 && ` +${bands.length - 2}`}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {dayEvents.length === 0 && (
                        <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '11px', textAlign: 'center' }}>
                          No events
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile: Horizontal scroll with ProfilePostCard (1.75 cards visible) */}
              <div className="mobile-scroll">
                {events.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)', width: '100%' }}>
                    No upcoming events found
                  </div>
                ) : (
                  events.map(event => (
                    <div key={event.id} className="mobile-event-card">
                      <ProfilePostCard
                        post={{ ...event, post_type: 'event' }}
                      />
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};


// ─── Updates (Articles) ───────────────────────────────────────────────────────

const UpdatesPanel = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(6);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await apiClient.get('/articles');
        if (response.data.success) {
          const sorted = response.data.articles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setArticles(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch articles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const displayedArticles = articles.slice(0, displayCount);
  const hasMore = displayCount < articles.length;

  return (
    <>
      <style>{`
        .updates-container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .updates-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        @media (max-width: 768px) {
          .updates-container {
            padding: 20px 0;
          }

          .updates-container h2 {
            font-size: 1.5rem !important;
            margin-bottom: 20px !important;
          }

          .updates-grid {
            grid-template-columns: 1fr;
            gap: 16px;
            max-width: 90vw;
          }
        }
      `}</style>

      <div className="updates-container">
        <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '24px', color: 'var(--text-light)' }}>Updates</h2>

        {loading ? (
          <div style={{ color: 'var(--text-muted)' }}>Loading updates...</div>
        ) : articles.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No recent articles.</div>
        ) : (
          <>
            <div className="updates-grid">
              {displayedArticles.map(article => (
                <ProfilePostCard
                  key={article.id}
                  post={{ ...article, post_type: 'article' }}
                />
              ))}
            </div>

            {hasMore && (
              <button
                onClick={() => setDisplayCount(prev => prev + 6)}
                className="btn btn-primary"
                style={{
                  marginTop: '24px',
                  padding: '12px 28px',
                  fontSize: '15px',
                  alignSelf: 'center'
                }}
              >
                Load More
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};


// ─── Home Page ────────────────────────────────────────────────────────────────

const HomePage = () => {
  const { user } = useAuth();

  return (
    <>
      <style>{`
        .homepage-container {
          min-height: 100vh;
          background-color: var(--bg-dark);
          display: flex;
          flex-direction: column;
        }

        .homepage-content {
          flex: 1;
          padding: 140px 32px 40px;
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        .homepage-layout {
          display: grid;
          grid-template-columns: 2fr 3fr;
          gap: 32px;
          align-items: start;
        }

        @media (max-width: 768px) {
          .homepage-content {
            padding: 125px 16px 40px 16px;
          }

          .homepage-layout {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .homepage-calendar-section {
            order: 1;
          }

          .homepage-updates-section {
            order: 2;
          }
        }
      `}</style>

      <div className="homepage-container">
        <Navbar />

        <div className="homepage-content">
          <div className="homepage-layout">
            {/* LEFT (Desktop) / BOTTOM (Mobile): Updates */}
            <div className="homepage-updates-section">
              <UpdatesPanel />
            </div>

            {/* RIGHT (Desktop) / TOP (Mobile): Event Calendar */}
            <div className="homepage-calendar-section">
              <EventCalendar
                userCity={user?.city}
                userState={user?.state}
                userTravelDistance={user?.travel_distance}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
